/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

/* starlight-iqd-fallback */
(function starlightIQDFallback() {
  document.documentElement.setAttribute('data-starlight-iqd-fallback', 'ready');
  const TARGET = 'IQD';
  const BASE = 'BHD';
  const STORAGE_KEY = 'bacurr_user_cur';
  const baseCache = new WeakMap();
  const numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  let applying = false;
  let scheduled = 0;

  const leafMoney = () =>
    [...document.querySelectorAll('span.money')].filter((el) => !el.querySelector('span.money'));

  const selectedCurrency = () => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      if (stored) return stored;
    } catch {}
    return document.querySelector('#baCurrSelector')?.value || BASE;
  };

  const readNumber = (value) => {
    const source = String(value ?? '').trim();
    if (!source) return NaN;
    const cleaned = source
      .replace(/<[^>]*>/g, '')
      .replace(/[^0-9,.-]/g, '')
      .replace(/,/g, '');
    if (!/[0-9]/.test(cleaned)) return NaN;
    const amount = Number(cleaned);
    return Number.isFinite(amount) ? amount : NaN;
  };

  const convert = (amount, from, to) => {
    const fn = window.baCurr?.Currency?.convert;
    if (typeof fn !== 'function') return NaN;
    const converted = Number(fn(amount, from, to));
    return Number.isFinite(converted) ? converted : NaN;
  };

  const rememberBase = (el, from = BASE) => {
    const visibleText = el.textContent?.trim() || '';
    const visibleBase = /\b(?:BD|BHD)\b/i.test(visibleText) ? readNumber(visibleText) : NaN;
    if (Number.isFinite(visibleBase)) {
      baseCache.set(el, visibleBase);
      el.dataset.starlightIqdBase = String(visibleBase);
      return visibleBase;
    }

    if (baseCache.has(el)) return baseCache.get(el);

    const saved = readNumber(el.dataset.starlightIqdBase);
    if (Number.isFinite(saved)) {
      baseCache.set(el, saved);
      return saved;
    }

    const jq = window.bacurr$;
    const $el = typeof jq === 'function' ? jq(el) : null;
    let base = readNumber($el?.data('bacurr-initVal'));

    if (!Number.isFinite(base)) {
      const visible = readNumber($el?.data('bacurr-origMoneyVal') ?? visibleText);
      base = from === BASE ? visible : convert(visible, from, BASE);
    }

    if (Number.isFinite(base)) {
      baseCache.set(el, base);
      el.dataset.starlightIqdBase = String(base);
    }
    return base;
  };

  const rememberAll = (from) => leafMoney().forEach((el) => rememberBase(el, from));

  const endApply = () => window.setTimeout(() => { applying = false; }, 0);

  const restoreBase = () => {
    applying = true;
    try {
      leafMoney().forEach((el) => {
        const base = rememberBase(el, selectedCurrency());
        if (!Number.isFinite(base)) return;
        el.textContent = `${base.toFixed(3)} BD`;
        el.classList.add('bacurr-money');
        delete el.dataset.starlightIqdRendered;
      });
    } finally {
      endApply();
    }
  };

  const syncPicker = () => {
    const select = document.querySelector('#baCurrSelector');
    if (select?.querySelector('option[value="IQD"]')) select.value = TARGET;
    const chosen = document.querySelector('.bacurr-choiceDesign .ba-chosen');
    if (chosen) chosen.textContent = TARGET;
    const flag = document.querySelector('.bacurr-choiceDesign .baCountry');
    if (flag) {
      [...flag.classList]
        .filter((name) => name.startsWith('baCountry-'))
        .forEach((name) => flag.classList.remove(name));
      flag.classList.add('baCountry-IQD');
    }
  };

  const renderIQD = () => {
    if (selectedCurrency() !== TARGET) return;
    applying = true;
    try {
      leafMoney().forEach((el) => {
        const base = rememberBase(el, BASE);
        if (!Number.isFinite(base)) return;
        const value = convert(base, BASE, TARGET);
        if (!Number.isFinite(value)) return;
        const text = `${numberFormat.format(Math.round(value))} IQD`;
        if (el.dataset.starlightIqdRendered === text && el.textContent.trim() === text) return;
        el.textContent = text;
        el.classList.add('bacurr-money');
        el.dataset.starlightIqdRendered = text;
      });
      syncPicker();
    } finally {
      endApply();
    }
  };

  const scheduleIQD = (delay = 40) => {
    window.clearTimeout(scheduled);
    scheduled = window.setTimeout(renderIQD, delay);
  };

  document.addEventListener('click', (event) => {
    const mover = event.target.closest?.('.bacurr-dropdown-choices .currMovers[rel]');
    if (!mover) return;

    const from = document.querySelector('#baCurrSelector')?.value || selectedCurrency();
    rememberAll(from);

    if (mover.getAttribute('rel') === TARGET) {
      window.setTimeout(renderIQD, 0);
      window.setTimeout(renderIQD, 900);
    } else {
      restoreBase();
    }
  }, true);

  document.addEventListener('change', () => {
    if (selectedCurrency() === TARGET) {
      window.setTimeout(renderIQD, 0);
      window.setTimeout(renderIQD, 900);
    }
  }, true);

  const observer = new MutationObserver((records) => {
    if (applying || selectedCurrency() !== TARGET) return;
    const relevant = records.some((record) => {
      const changedMoneyText =
        record.type === 'characterData' &&
        record.target.parentElement?.closest('span.money');
      if (changedMoneyText) return true;
      return [...record.addedNodes].some((node) =>
        (node.nodeType === Node.TEXT_NODE &&
          node.parentElement?.closest('span.money')) ||
        (node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('span.money') || node.querySelector?.('span.money')))
      );
    });
    if (relevant) scheduleIQD();
  });

  const start = () => {
    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    let tries = 0;
    const boot = window.setInterval(() => {
      const ready =
        typeof window.baCurr?.Currency?.convert === 'function' &&
        typeof window.bacurr$ === 'function';
      if (ready) {
        window.clearInterval(boot);
        window.bacurr$(document).ajaxSuccess(() => {
          if (selectedCurrency() === TARGET) scheduleIQD();
        });
        if (selectedCurrency() === TARGET) scheduleIQD(0);
      } else if (++tries >= 80) {
        window.clearInterval(boot);
      }
    }, 250);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
